import React from 'react'
import { useTranslation } from 'react-i18next'
import { useDraggable } from '@dnd-kit/core'

import { formatDate } from 'helpers/helpers'

function TasksBoardCard({
	task,
	column = {},
	isOverlay = false,
	isMoving = false,
	canManage = false,
	canEdit = false,
	onOpenDetail = null,
	onEdit = null,
	onDelete = null,
}) {
	const { t } = useTranslation()

	const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
		id: 'task-' + task.id,
		data: { task: task, fromStatusId: column.id },
		disabled: isOverlay || isMoving,
	})

	function returnClassName() {
		var className = 'tasks-card'
		if (isOverlay) className += ' is-overlay'
		if (isDragging) className += ' is-dragging'
		if (task.is_overdue) className += ' is-overdue'
		return className
	}

	function returnEmployees() {
		if (!task.users || task.users.length === 0) return ''
		return task.users.map(item => item.name || item.login).join(', ')
	}

	function returnRestaurant() {
		if (!task.pos_id) return task.client_login || ''
		if (!task.client_login) return (task.pos_short_name || task.pos_name || '') + ' (ID: ' + task.pos_id + ')'
		return task.client_login + ' (ID: ' + task.pos_id + ')'
	}

	function handleAction(e, action) {
		e.stopPropagation()
		if (typeof action === 'function') action(task)
	}

	// Пустые поля на карточку не выводим: строки с «-» только шумят, всё остальное есть в детальной.
	// У задач на подключение точки ещё нет — вместо неё заполнены контакты клиента
	const metaRows = [
		{ 'label': t('employee'), 'value': returnEmployees() },
		{ 'label': t('organization'), 'value': task.client_name },
		{ 'label': t('task_restaurant'), 'value': returnRestaurant() },
		{ 'label': t('phone'), 'value': task.client_phone },
		{ 'label': t('address'), 'value': task.address },
	].filter(row => row.value)

	return (
		<div ref={isOverlay ? null : setNodeRef}
			className={returnClassName()}
			onClick={() => { if (!isOverlay && typeof onOpenDetail === 'function') onOpenDetail(task) }}
			{...(isOverlay ? {} : listeners)}
			{...(isOverlay ? {} : attributes)}>

			{!isOverlay && (canEdit || canManage) &&
				<div className="tasks-card-actions" onPointerDown={(e) => e.stopPropagation()}>
					{canEdit &&
						<button type="button" className="tasks-card-action"
							title={t('edit')}
							onClick={(e) => handleAction(e, onEdit)}>
							<i className="uil uil-edit-alt"></i>
						</button>
					}
					{canManage &&
						<button type="button" className="tasks-card-action is-danger"
							title={t('delete')}
							onClick={(e) => handleAction(e, onDelete)}>
							<i className="uil uil-trash-alt"></i>
						</button>
					}
				</div>
			}

			<div className="tasks-card-top">
				<div className="tasks-card-title" title={task.name}>{task.name}</div>
				<div className="tasks-card-badge"
					style={{ background: (column.color || '#5b73e8') + '1f', color: column.color || '#5b73e8' }}>
					<i className="uil uil-clock"></i>
				</div>
			</div>

			<div className="tasks-card-info">
				<span className="tasks-card-id">ID {task.id}</span>
				{/* Задачу завёл крон по состоянию клиента — руками её никто не создавал */}
				{task.source === 'system' &&
					<span className="tasks-card-auto" title={t('task_source_system_hint')}>
						<i className="uil uil-bolt-alt"></i>
						{t('task_source_system')}
					</span>
				}
				{/* Лид ведут вдвоём: видно прямо с доски, что задача общая */}
				{task.users?.length > 1 &&
					<span className="tasks-card-shared" title={returnEmployees()}>
						<i className="uil uil-users-alt"></i>
						{task.users.length}
					</span>
				}
				{task.notes_count > 0 &&
					<span className="tasks-card-notes" title={t('task_notes')}>
						<i className="uil uil-comment-alt-lines"></i>
						{task.notes_count}
					</span>
				}
				{task.deadline &&
					<span className={'tasks-card-date' + (task.is_overdue ? ' is-overdue' : '')}>
						{formatDate(task.deadline, 'dd.MM.yyyy')}
					</span>
				}
			</div>

			{metaRows.length > 0 &&
				<>
					<div className="tasks-card-separator"></div>

					<div className="tasks-card-meta">
						{metaRows.map((row, index) => (
							<React.Fragment key={index}>
								<div className="tasks-card-meta-label">{row.label}</div>
								<div className="tasks-card-meta-value" title={row.value}>{row.value}</div>
							</React.Fragment>
						))}
					</div>
				</>
			}

			{/* Примечание — описание задачи, чтобы читать его прямо с доски, не открывая карточку */}
			{task.description &&
				<div className="tasks-card-note">
					<div className="tasks-card-meta-label">{t('note')}</div>
					<div className="tasks-card-note-text" title={task.description}>{task.description}</div>
				</div>
			}

			{/* Последнее примечание — чем закончился прошлый звонок. Вся лента в детальной */}
			{task.last_note &&
				<div className="tasks-card-note">
					<div className="tasks-card-meta-label">{t('task_note_last')}</div>
					<div className="tasks-card-note-text" title={task.last_note}>{task.last_note}</div>
				</div>
			}
		</div>
	)
}

export default TasksBoardCard
